// Card — raised surface with status-stripe variants and a hover state.
//
// Stripe variants per spec §7.0 + Pencil iteration 2:
//   default     — no stripe, default border-subtle
//   in_progress — Sage 3px left stripe (active commitments)
//   overdue     — Ember 3px left stripe (past-due commitments)
//   completed   — Gold 3px left stripe (recently done; fades after a day)
//   blocked     — Stone 3px left stripe (waiting on something)
//
// Hover state: border becomes Teal, subtle shadow lift via shadow-hover.

import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { cn } from "./cn";

export type CardVariant = "default" | "in_progress" | "overdue" | "completed" | "blocked";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  children?: ReactNode;
}

// Each variant maps to a left-border color. The width is a constant 3px
// applied via Tailwind's `border-l-[3px]` arbitrary-value (not a hex code,
// not a forbidden raw color — it's a width). For `default` we keep a 1px
// border-subtle so the card still has a defined edge.
const VARIANT_STRIPE: Record<CardVariant, string> = {
  default: "border-l border-l-border-subtle",
  in_progress: "border-l-[3px] border-l-sage",
  overdue: "border-l-[3px] border-l-ember",
  completed: "border-l-[3px] border-l-gold",
  blocked: "border-l-[3px] border-l-blocked",
};

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { variant = "default", className, children, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      data-variant={variant}
      className={cn(
        // Base chrome — paper-white raised bg, subtle 1px border on the
        // non-stripe sides, generous padding (5 = 20px per spec).
        "rounded-lg border border-border-subtle border-l-0 bg-raised p-5",
        // Hover: Teal edge + subtle shadow lift. Transitions via the
        // tokens in tailwind.config.ts so the timing stays calm/fast.
        "transition-base ease-out hover:border-border-hover hover:shadow-hover",
        VARIANT_STRIPE[variant],
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
});
